import { PageContainer } from "@/components/layout/PageContainer";
import { NicknameSelectionModal } from "@/components/NicknameSelectionModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getIdToken } from "@/lib/firebase";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type {
  ActivityLog,
  Bookmark as BookmarkType,
  MarketplaceItem,
  Vocabulary,
} from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Activity,
  Bookmark,
  BookmarkCheck,
  BookOpen,
  Building2,
  CreditCard,
  Crown,
  GraduationCap,
  Loader2,
  Newspaper,
  Package,
  Settings,
  ShoppingBag,
  Trash2,
  User,
  Users,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

type MeetingSummary = {
  id: number | string;
  title: string;
  state?: string | null;
  suburb?: string | null;
  ageRange?: string | null;
  coverImage?: string | null;
  category?: string | null;
  members?: number;
  posts?: number;
  role?: string;
  joinedAt?: string;
};

type MyMeetingsApiResponse = {
  ok: boolean;
  data: {
    created: MeetingSummary[];
    joined: MeetingSummary[];
  };
};

export default function MyPage() {
  const { user, userProfile, loading, isAuthenticated, refetchProfile } =
    useAuth();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("bookmarks");
  const { toast } = useToast();
  const [showNicknameModal, setShowNicknameModal] = useState(false);

  // Fetch user info including nickname
  // Use unified queryKey ["me"] for consistent cache invalidation
  const { data: userInfo, refetch: refetchUserInfo } = useQuery({
    queryKey: ["me", user?.uid], // Unified key format
    queryFn: async () => {
      if (!user) return null;
      const token = await getIdToken();
      if (!token) return null;
      const res = await fetch("/api/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!user && isAuthenticated,
    staleTime: 0, // Always consider data stale, refetch immediately
    gcTime: 0, // Don't cache (formerly cacheTime)
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setLocation("/");
    }
  }, [loading, isAuthenticated, setLocation]);

  const { data: bookmarks = [], isLoading: bookmarksLoading } = useQuery<
    BookmarkType[]
  >({
    queryKey: ["/api/bookmarks", user?.uid],
    queryFn: async () => {
      const params = new URLSearchParams({ userId: user?.uid || "" });
      const res = await fetch(`/api/bookmarks?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load bookmarks");
      return res.json();
    },
    enabled: !!user?.uid,
  });

  // 실제 라우트는 /api/activity가 아니라 /api/activity-logs이고, 본인 활동 조회에는
  // 실제 Firebase ID 토큰이 필요하다(관리자 전용이던 라우트를 본인 조회도 허용하도록
  // 같이 수정함 - functions/api/activity-logs/index.ts 참고).
  const { data: activityLogs = [], isLoading: activityLoading } = useQuery<
    ActivityLog[]
  >({
    queryKey: ["/api/activity-logs", user?.uid],
    queryFn: async () => {
      const { getIdToken } = await import("@/lib/firebase");
      const token = await getIdToken();
      const res = await fetch("/api/activity-logs", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to load activity logs");
      const json = await res.json();
      return json.items || [];
    },
    enabled: !!user?.uid,
  });

  const { data: vocabulary = [], isLoading: vocabLoading } = useQuery<
    Vocabulary[]
  >({
    queryKey: ["/api/vocabulary", user?.uid],
    queryFn: async () => {
      const res = await fetch(`/api/vocabulary/${user?.uid}`);
      if (!res.ok) throw new Error("Failed to load vocabulary");
      return res.json();
    },
    enabled: !!user?.uid,
  });

  const { data: myProducts = [], isLoading: productsLoading } = useQuery<
    MarketplaceItem[]
  >({
    queryKey: ["/api/marketplace/items", "my-products", user?.uid],
    queryFn: async () => {
      if (!user?.uid) return [];
      const response = await fetch(`/api/marketplace/items?userId=${user.uid}`);
      if (!response.ok) throw new Error("Failed to fetch products");
      return response.json();
    },
    enabled: !!user?.uid,
  });

  const { data: myMeetingsData, isLoading: myMeetingsLoading } =
    useQuery<MyMeetingsApiResponse>({
      queryKey: ["/api/me/meetings", user?.uid],
      queryFn: async () => {
        if (!user?.uid) return { ok: true, data: { created: [], joined: [] } };
        const token = await getIdToken();
        if (!token) throw new Error("로그인이 필요합니다");
        const res = await fetch("/api/me/meetings", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          const errorText = await res.text();
          console.error("Failed to fetch my meetings:", res.status, errorText);
          throw new Error("내 모임 정보를 불러오지 못했습니다");
        }
        const json = await res.json();

        // API 응답 구조: { ok: true, data: { created: [], joined: [] } }
        let payload: { created: any[]; joined: any[] } = {
          created: [],
          joined: [],
        };

        if (json.ok && json.data) {
          // 중첩 구조: { ok: true, data: { created: [], joined: [] } }
          if (json.data.created || json.data.joined) {
            payload = json.data;
          }
          // 더 중첩된 경우: { ok: true, data: { ok: true, data: {...} } }
          else if (
            json.data.data &&
            (json.data.data.created || json.data.data.joined)
          ) {
            payload = json.data.data;
          }
        }
        // 직접 구조: { created: [], joined: [] }
        else if (json.created || json.joined) {
          payload = json;
        }

        console.log("My meetings payload:", payload);
        return { ok: true, data: payload };
      },
      enabled: !!user?.uid,
    });

  const myMeetingPayload =
    myMeetingsData?.ok && myMeetingsData.data
      ? myMeetingsData.data
      : { created: [], joined: [] };
  const createdMeetings = Array.isArray(myMeetingPayload.created)
    ? myMeetingPayload.created
    : [];
  const joinedMeetings = Array.isArray(myMeetingPayload.joined)
    ? myMeetingPayload.joined
    : [];

  const leaveMeetingMutation = useMutation({
    mutationFn: async (meetingId: number | string) => {
      const token = await getIdToken();
      if (!token) throw new Error("로그인이 필요합니다");
      const res = await fetch(`/api/meetings/${meetingId}/leave`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("모임 탈퇴에 실패했습니다");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/me/meetings", user?.uid],
      });
      toast({
        title: "모임 탈퇴 완료",
        description: "모임에서 성공적으로 탈퇴했습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "모임 탈퇴 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 북마크 삭제 mutation
  const deleteBookmarkMutation = useMutation({
    mutationFn: async (bookmarkId: string) => {
      return await apiRequest("DELETE", `/api/bookmarks/${bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/bookmarks", user?.uid],
      });
      toast({
        title: "찜하기 삭제 완료",
        description: "찜하기가 삭제되었습니다",
      });
    },
    onError: (error: any) => {
      toast({
        title: "삭제 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (loading || !user || !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isPremium = userProfile.role === "premium";

  // Group bookmarks by type
  const bookmarksByType = {
    article: bookmarks.filter((b) => b.itemType === "article"),
    video: bookmarks.filter((b) => b.itemType === "video"),
    product: bookmarks.filter((b) => b.itemType === "product"),
    business: bookmarks.filter((b) => b.itemType === "business"),
    topic: bookmarks.filter((b) => b.itemType === "topic"),
  };

  return (
    <PageContainer size="default">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">마이페이지</h1>
          <p className="text-muted-foreground">
            학습 현황과 찜한 콘텐츠를 관리하세요
          </p>
        </div>
        {isPremium && (
          <Badge variant="default" className="text-sm px-3 py-1">
            <Crown className="h-4 w-4 mr-1" />
            프리미엄
          </Badge>
        )}
      </div>

      {/* Nickname Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            닉네임
          </CardTitle>
          <CardDescription>동네에서 사용할 별명을 설정하세요</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              {userInfo?.nicknameDisplay ||
              userInfo?.nickname ||
              userInfo?.communityDisplayName ? (
                <div>
                  <p className="text-lg font-semibold">
                    {userInfo.nicknameDisplay ||
                      userInfo.nickname ||
                      userInfo.communityDisplayName}
                  </p>
                  {userInfo.suburb && (
                    <p className="text-sm text-muted-foreground">
                      {userInfo.suburb}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  닉네임이 설정되지 않았습니다
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowNicknameModal(true)}
            >
              {userInfo?.nicknameDisplay ||
              userInfo?.nickname ||
              userInfo?.communityDisplayName
                ? "변경하기"
                : "설정하기"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />내 모임 관리
          </CardTitle>
          <CardDescription>
            참여 중인 모임을 확인하고 탈퇴하거나, 내가 만든 모임을 관리하세요.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold">참여한 모임</h3>
            </div>
            {myMeetingsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={`joined-skeleton-${i}`}
                    className="h-16 rounded-md bg-muted animate-pulse"
                  />
                ))}
              </div>
            ) : joinedMeetings.length > 0 ? (
              <div className="space-y-3">
                {joinedMeetings.map((meeting) => (
                  <div
                    key={`joined-${meeting.id}`}
                    className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-semibold">{meeting.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {meeting.suburb
                          ? `${meeting.suburb}, ${meeting.state || ""}`
                          : meeting.state || "지역 미지정"}
                        {meeting.ageRange
                          ? ` · 나이대 ${meeting.ageRange}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="whitespace-nowrap"
                      >
                        <a href={`/meetings/${meeting.id}`}>보기</a>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => leaveMeetingMutation.mutate(meeting.id)}
                        disabled={leaveMeetingMutation.isPending}
                      >
                        탈퇴
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                참여한 모임이 없습니다.
              </p>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold">내가 만든 모임</h3>
            </div>
            {myMeetingsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={`created-skeleton-${i}`}
                    className="h-16 rounded-md bg-muted animate-pulse"
                  />
                ))}
              </div>
            ) : createdMeetings.length > 0 ? (
              <div className="space-y-3">
                {createdMeetings.map((meeting) => (
                  <div
                    key={`created-${meeting.id}`}
                    className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-semibold">{meeting.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {meeting.suburb
                          ? `${meeting.suburb}, ${meeting.state || ""}`
                          : meeting.state || "지역 미지정"}
                        {meeting.ageRange
                          ? ` · 나이대 ${meeting.ageRange}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <a href={`/meetings/${meeting.id}`}>보기</a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                아직 만든 모임이 없습니다.
              </p>
            )}
          </section>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          <TabsTrigger
            value="bookmarks"
            className="flex items-center gap-2"
            data-testid="tab-bookmarks"
          >
            <Bookmark className="h-4 w-4" />
            <span className="hidden sm:inline">찜한 항목</span>
          </TabsTrigger>
          <TabsTrigger
            value="my-products"
            className="flex items-center gap-2"
            data-testid="tab-my-products"
          >
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">내 상품</span>
          </TabsTrigger>
          <TabsTrigger
            value="learning"
            className="flex items-center gap-2"
            data-testid="tab-learning"
          >
            <GraduationCap className="h-4 w-4" />
            <span className="hidden sm:inline">영어 학습</span>
          </TabsTrigger>
          <TabsTrigger
            value="subscription"
            className="flex items-center gap-2"
            data-testid="tab-subscription"
          >
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">구독 관리</span>
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            className="flex items-center gap-2"
            data-testid="tab-settings"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">설정</span>
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="flex items-center gap-2"
            data-testid="tab-activity"
          >
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">활동 내역</span>
          </TabsTrigger>
        </TabsList>

        {/* Bookmarks Tab */}
        <TabsContent value="bookmarks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookmarkCheck className="h-5 w-5" />
                찜한 항목 ({bookmarks.length}개)
              </CardTitle>
              <CardDescription>관심 있는 콘텐츠를 모아보세요</CardDescription>
            </CardHeader>
            <CardContent>
              {bookmarksLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : bookmarks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bookmark className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>찜한 항목이 없습니다</p>
                  <p className="text-sm mt-1">
                    마음에 드는 콘텐츠를 찜해보세요!
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Articles */}
                  {bookmarksByType.article.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <Newspaper className="h-4 w-4" />
                        뉴스 기사 ({bookmarksByType.article.length})
                      </h3>
                      <div className="space-y-2">
                        {bookmarksByType.article.map((bookmark) => (
                          <div
                            key={bookmark.id}
                            className="flex items-center justify-between p-3 border rounded-md hover-elevate"
                            data-testid={`bookmark-article-${bookmark.itemId}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {bookmark.itemTitle || "제목 없음"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(
                                  bookmark.createdAt
                                ).toLocaleDateString("ko-KR")}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setLocation(`/article/${bookmark.itemId}`)
                                }
                                data-testid={`button-view-${bookmark.itemId}`}
                              >
                                보기
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  deleteBookmarkMutation.mutate(bookmark.id)
                                }
                                disabled={deleteBookmarkMutation.isPending}
                                data-testid={`button-delete-${bookmark.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {bookmarksByType.video.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <Video className="h-4 w-4" />
                        동영상 ({bookmarksByType.video.length})
                      </h3>
                      <div className="space-y-2">
                        {bookmarksByType.video.map((bookmark) => (
                          <div
                            key={bookmark.id}
                            className="flex items-center justify-between p-3 border rounded-md hover-elevate"
                            data-testid={`bookmark-video-${bookmark.itemId}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {bookmark.itemTitle || "제목 없음"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(
                                  bookmark.createdAt
                                ).toLocaleDateString("ko-KR")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">영상</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  deleteBookmarkMutation.mutate(bookmark.id)
                                }
                                disabled={deleteBookmarkMutation.isPending}
                                data-testid={`button-delete-${bookmark.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Products */}
                  {bookmarksByType.product.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <ShoppingBag className="h-4 w-4" />
                        상품 ({bookmarksByType.product.length})
                      </h3>
                      <div className="space-y-2">
                        {bookmarksByType.product.map((bookmark) => (
                          <div
                            key={bookmark.id}
                            className="flex items-center justify-between p-3 border rounded-md hover-elevate"
                            data-testid={`bookmark-product-${bookmark.itemId}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {bookmark.itemTitle || "제목 없음"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(
                                  bookmark.createdAt
                                ).toLocaleDateString("ko-KR")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">상품</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  deleteBookmarkMutation.mutate(bookmark.id)
                                }
                                disabled={deleteBookmarkMutation.isPending}
                                data-testid={`button-delete-${bookmark.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Topics */}
                  {bookmarksByType.topic.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <BookOpen className="h-4 w-4" />
                        생활문제 ({bookmarksByType.topic.length})
                      </h3>
                      <div className="space-y-2">
                        {bookmarksByType.topic.map((bookmark) => (
                          <div
                            key={bookmark.id}
                            className="flex items-center justify-between p-3 border rounded-md hover-elevate"
                            data-testid={`bookmark-topic-${bookmark.itemId}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {bookmark.itemTitle || "제목 없음"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(
                                  bookmark.createdAt
                                ).toLocaleDateString("ko-KR")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setLocation(`/topics/${bookmark.itemId}`)
                                }
                                data-testid={`button-view-${bookmark.itemId}`}
                              >
                                보기
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  deleteBookmarkMutation.mutate(bookmark.id)
                                }
                                disabled={deleteBookmarkMutation.isPending}
                                data-testid={`button-delete-${bookmark.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Businesses */}
                  {bookmarksByType.business.length > 0 && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <Building2 className="h-4 w-4" />
                        업체 ({bookmarksByType.business.length})
                      </h3>
                      <div className="space-y-2">
                        {bookmarksByType.business.map((bookmark) => (
                          <div
                            key={bookmark.id}
                            className="flex items-center justify-between p-3 border rounded-md hover-elevate"
                            data-testid={`bookmark-business-${bookmark.itemId}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {bookmark.itemTitle || "제목 없음"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(
                                  bookmark.createdAt
                                ).toLocaleDateString("ko-KR")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">업체</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  deleteBookmarkMutation.mutate(bookmark.id)
                                }
                                disabled={deleteBookmarkMutation.isPending}
                                data-testid={`button-delete-${bookmark.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Products Tab */}
        <TabsContent value="my-products" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  내가 등록한 상품 ({myProducts.length}개)
                </CardTitle>
                <CardDescription>등록한 상품을 관리하세요</CardDescription>
              </div>
              <Button
                onClick={() => setLocation("/marketplace/register")}
                data-testid="button-add-product"
              >
                상품 등록
              </Button>
            </CardHeader>
            <CardContent>
              {productsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : myProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>등록한 상품이 없습니다</p>
                  <p className="text-sm mt-1">첫 상품을 등록해보세요!</p>
                  <Button
                    onClick={() => setLocation("/marketplace/register")}
                    className="mt-4"
                    data-testid="button-register-first"
                  >
                    상품 등록하기
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-start justify-between p-4 border rounded-md hover-elevate"
                      data-testid={`product-${product.id}`}
                    >
                      <div className="flex gap-4 flex-1">
                        <img
                          src={
                            product.imageUrl ||
                            "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop"
                          }
                          alt={product.title}
                          className="w-20 h-20 object-cover rounded bg-muted"
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop";
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold">
                              {product.businessName}
                            </p>
                            <Badge
                              variant={
                                product.status === "active"
                                  ? "default"
                                  : product.status === "pending"
                                  ? "secondary"
                                  : "destructive"
                              }
                              data-testid={`status-${product.id}`}
                            >
                              {product.status === "pending"
                                ? "승인대기"
                                : product.status === "active"
                                ? "활성"
                                : product.status === "rejected"
                                ? "거부됨"
                                : product.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {product.titleKo || product.title}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">
                              ${Number(product.salePrice).toFixed(2)}
                            </span>
                            {product.originalPrice && (
                              <span className="line-through text-xs">
                                ${Number(product.originalPrice).toFixed(2)}
                              </span>
                            )}
                            <span>조회 {product.views || 0}</span>
                            <span>클릭 {product.clicks || 0}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setLocation(`/marketplace/${product.id}`)
                        }
                        data-testid={`button-view-product-${product.id}`}
                      >
                        보기
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Learning Tab */}
        <TabsContent value="learning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                영어 학습 현황
              </CardTitle>
              <CardDescription>
                나만의 단어장과 학습 진도를 확인하세요
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-secondary rounded-md">
                  <p
                    className="text-2xl font-bold text-primary"
                    data-testid="text-vocab-count"
                  >
                    {vocabulary.length}
                  </p>
                  <p className="text-sm text-muted-foreground">저장된 단어</p>
                </div>
                <div className="text-center p-4 bg-secondary rounded-md">
                  <p
                    className="text-2xl font-bold text-primary"
                    data-testid="text-lookups-count"
                  >
                    {userProfile.usageTracking.wordLookups}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    단어 조회 (오늘)
                  </p>
                </div>
                <div className="text-center p-4 bg-secondary rounded-md">
                  <p
                    className="text-2xl font-bold text-primary"
                    data-testid="text-quizzes-count"
                  >
                    {userProfile.usageTracking.quizzesTaken}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    퀴즈 완료 (이번 주)
                  </p>
                </div>
              </div>

              <div className="pt-4 flex gap-2 flex-wrap">
                <Button
                  variant="default"
                  onClick={() => setLocation("/vocabulary")}
                  data-testid="button-go-vocabulary"
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  단어장 가기
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setLocation("/flashcards")}
                  data-testid="button-go-flashcards"
                >
                  플래시 카드
                </Button>
              </div>

              {!isPremium && vocabulary.length >= 20 && (
                <div className="bg-primary/10 p-4 rounded-md border border-primary/20">
                  <div className="flex items-start gap-2">
                    <Crown className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">단어장이 가득 찼습니다!</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        무료 회원은 최대 20개까지만 저장 가능합니다.
                        프리미엄으로 업그레이드하여 무제한으로 저장하세요.
                      </div>
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={() => setLocation("/pricing")}
                        data-testid="button-upgrade-from-learning"
                      >
                        프리미엄 업그레이드
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                구독 관리
              </CardTitle>
              <CardDescription>
                현재 구독 정보 및 결제 내역을 확인하세요
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-md p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">현재 플랜</h3>
                    <p className="text-sm text-muted-foreground">
                      {userProfile.role === "platinum"
                        ? "플래티넘 회원"
                        : userProfile.role === "premium"
                        ? "프리미엄 회원"
                        : "무료 회원"}
                    </p>
                  </div>
                  {userProfile.role === "platinum" ? (
                    <Badge
                      variant="default"
                      className="text-sm px-3 py-1 bg-gradient-to-r from-yellow-600 to-yellow-400"
                    >
                      <Crown className="h-4 w-4 mr-1" />
                      플래티넘
                    </Badge>
                  ) : userProfile.role === "premium" ? (
                    <Badge variant="default" className="text-sm px-3 py-1">
                      <Crown className="h-4 w-4 mr-1" />
                      프리미엄
                    </Badge>
                  ) : (
                    <Badge variant="secondary">무료</Badge>
                  )}
                </div>

                {isPremium ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">가입일</span>
                      <span className="font-medium">
                        {new Date(userProfile.createdAt).toLocaleDateString(
                          "ko-KR"
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">월 결제액</span>
                      <span className="font-medium">
                        {userProfile.role === "platinum" ? "$6.99" : "$3.99"}
                      </span>
                    </div>
                    {userProfile.role === "platinum" && (
                      <div className="mt-2 p-2 bg-green-50 dark:bg-green-950 rounded text-green-700 dark:text-green-300 text-xs">
                        ✨ 광고 없는 프리미엄 경험을 이용 중입니다
                      </div>
                    )}
                    {userProfile.role === "premium" && (
                      <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded text-blue-700 dark:text-blue-300 text-xs">
                        💡 플래티넘으로 업그레이드하면 광고가 제거됩니다
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4">
                    <p className="text-sm mb-3">
                      프리미엄으로 업그레이드하여 더 많은 기능을 이용하세요:
                    </p>
                    <ul className="space-y-1 text-sm mb-4">
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        무제한 단어 저장 및 조회
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        무제한 리딩 퀴즈 및 해설
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        무제한 플래시카드 학습
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        완전한 이메일 학습 콘텐츠
                      </li>
                    </ul>
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={() => setLocation("/pricing")}
                      data-testid="button-upgrade-subscription"
                    >
                      <Crown className="h-4 w-4 mr-2" />
                      프리미엄 업그레이드
                    </Button>
                  </div>
                )}
              </div>

              {isPremium && (
                <div className="text-center">
                  <Button
                    variant="outline"
                    onClick={() => setLocation("/profile")}
                    data-testid="button-manage-billing"
                  >
                    결제 관리
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                설정
              </CardTitle>
              <CardDescription>
                알림 및 개인화 설정을 관리하세요
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setLocation("/profile")}
                  data-testid="button-profile-settings"
                >
                  프로필 및 이메일 설정
                </Button>
                <div className="text-sm text-muted-foreground">
                  <p>더 많은 설정 옵션이 곧 추가됩니다.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                활동 내역
              </CardTitle>
              <CardDescription>최근 활동을 확인하세요</CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>활동 내역이 없습니다</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activityLogs.slice(0, 20).map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-3 border rounded-md"
                      data-testid={`activity-log-${log.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          {log.activityType === "view_article" && "기사 조회"}
                          {log.activityType === "bookmark_add" && "찜하기 추가"}
                          {log.activityType === "bookmark_remove" &&
                            "찜하기 취소"}
                          {log.activityType === "word_lookup" && "단어 조회"}
                          {log.activityType === "quiz_complete" && "퀴즈 완료"}
                        </p>
                        {log.itemTitle && (
                          <p className="text-sm text-muted-foreground truncate">
                            {log.itemTitle}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                        {new Date(log.createdAt).toLocaleDateString("ko-KR")}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Nickname Selection Modal */}
      <NicknameSelectionModal
        open={showNicknameModal}
        onOpenChange={setShowNicknameModal}
        onSuccess={async () => {
          console.log("[MyPage] 닉네임 저장 성공 → invalidateQueries 실행");

          try {
            // Step 1: Invalidate the query cache (must use exact same queryKey)
            await queryClient.invalidateQueries({
              queryKey: ["me", user?.uid],
            });

            console.log("[MyPage] invalidateQueries 완료 → refetch 시작");

            // Step 2: Force immediate refetch (await is critical!)
            const refetchResult = await refetchUserInfo();

            console.log("[MyPage] refetch 결과:", {
              data: refetchResult.data,
              nickname: refetchResult.data?.nickname,
              nicknameDisplay: refetchResult.data?.nicknameDisplay,
              communityDisplayName: refetchResult.data?.communityDisplayName,
            });

            // Step 3: Also refetch profile
            await refetchProfile();

            toast({
              title: "닉네임이 설정되었습니다",
              description: "변경사항이 적용되었습니다",
            });
          } catch (error) {
            console.error("[MyPage] Error refetching user info:", error);
            toast({
              title: "닉네임이 설정되었습니다",
              description: "화면을 새로고침해주세요",
            });
          }
        }}
        currentNickname={
          userInfo?.nicknameDisplay ||
          userInfo?.nickname ||
          userInfo?.communityDisplayName
        }
      />
    </PageContainer>
  );
}
